"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { LayoutDashboard, LogIn, Plus, Menu, X, LogOut, Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuthStore, useWizardStore } from "@/store";
import { cn } from "@/lib/utils";

export function Navbar() {
  const pathname = usePathname();
  const { user, logout } = useAuthStore();
  const [open, setOpen] = useState(false);

  // Esconde o Navbar nas páginas públicas dos gifts (/p/...) — elas têm
  // identidade visual própria do template e Navbar destoa.
  if (pathname.startsWith("/p/")) return null;

  // Define o "modo" da Navbar pela combinação rota + auth:
  //  • marketing  → na landing, sem user logado. Foco em conversão.
  //  • marketingAuth → na landing, com user logado. CTA "Ir pro painel".
  //  • app        → dentro do produto (dashboard, criar, etc), com user.
  //                 Marketing zero, foco em produtividade.
  const isInProductRoute =
    pathname.startsWith("/dashboard") || pathname.startsWith("/criar");

  const mode: "marketing" | "marketingAuth" | "app" =
    user && isInProductRoute ? "app" : user ? "marketingAuth" : "marketing";

  const firstName = (user?.name || "").trim().split(" ")[0];
  const initial = firstName ? firstName[0].toUpperCase() : "I";

  return (
    <header className="sticky top-0 z-50 border-b border-rose-100 bg-white/90 backdrop-blur-sm">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link
          href={user ? "/dashboard" : "/"}
          aria-label="ILovePage — início"
          className="inline-flex items-center"
        >
          <Image
            src="/brand/logo-horizontal.png"
            alt="ILovePage"
            width={2172}
            height={724}
            priority
            className="h-11 sm:h-12 w-auto"
          />
        </Link>

        {/* ─── Desktop nav ──────────────────────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-5">
          {mode === "marketing" && (
            <>
              <Link
                href="/#precos"
                className="text-sm font-medium text-[#6B7280] hover:text-[#881337] transition-colors"
              >
                Planos
              </Link>
              <Link
                href="/login"
                className="text-sm font-medium text-[#6B7280] hover:text-[#881337] transition-colors"
              >
                Entrar
              </Link>
              <Button size="sm" asChild>
                <Link href="/criar">
                  <Plus className="h-4 w-4 mr-1" />
                  Criar ILovePage
                </Link>
              </Button>
            </>
          )}

          {mode === "marketingAuth" && (
            <>
              <Link
                href="/dashboard"
                className="text-sm font-medium text-[#6B7280] hover:text-[#881337] transition-colors"
              >
                Minhas páginas
              </Link>
              <Button size="sm" asChild>
                <Link href="/dashboard">
                  <Sparkles className="h-4 w-4 mr-1" />
                  Ir pro Painel
                </Link>
              </Button>
              <UserChip firstName={firstName} initial={initial} onLogout={logout} />
            </>
          )}

          {mode === "app" && (
            <>
              <Link
                href="/dashboard"
                className={cn(
                  "flex items-center gap-1.5 text-sm font-medium transition-colors",
                  pathname.startsWith("/dashboard")
                    ? "text-[#881337]"
                    : "text-[#6B7280] hover:text-[#881337]"
                )}
              >
                <LayoutDashboard className="h-4 w-4" />
                Minhas páginas
              </Link>
              <Button variant="outline" size="sm" asChild>
                <Link
                  href="/criar"
                  onClick={() => useWizardStore.getState().reset()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Nova ILovePage
                </Link>
              </Button>
              <UserChip firstName={firstName} initial={initial} onLogout={logout} />
            </>
          )}
        </nav>

        {/* ─── Mobile menu toggle ───────────────────────────────────────── */}
        <button
          className="md:hidden inline-flex items-center justify-center h-11 w-11 -mr-2 rounded-lg text-gray-500 hover:bg-rose-50 active:bg-rose-100 transition-colors"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* ─── Mobile menu ────────────────────────────────────────────────── */}
      {open && (
        <div className="md:hidden border-t border-rose-100 bg-white px-4 py-3 flex flex-col gap-1">
          {user ? (
            <>
              <div className="flex items-center gap-3 py-2 px-2 mb-1 border-b border-rose-50 pb-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FFE4E6] text-[#881337] font-black text-sm">
                  {initial}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1F2937] truncate">
                    {firstName ? `Olá, ${firstName}!` : "Logado"}
                  </p>
                  {user.email && (
                    <p className="text-xs text-[#6B7280] truncate">{user.email}</p>
                  )}
                </div>
              </div>
              <Link
                href="/dashboard"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 py-3 px-2 rounded-lg active:bg-rose-50"
              >
                <LayoutDashboard className="h-4 w-4 text-[#E11D48]" />
                Minhas páginas
              </Link>
              <Link
                href="/criar"
                onClick={() => {
                  useWizardStore.getState().reset();
                  setOpen(false);
                }}
                className="mt-1"
              >
                <Button className="w-full" size="default">
                  <Plus className="h-4 w-4 mr-1" />
                  Nova ILovePage
                </Button>
              </Link>
              <button
                onClick={() => { logout(); setOpen(false); }}
                className="flex items-center gap-2 text-sm text-gray-400 active:text-[#E11D48] text-left py-3 px-2 rounded-lg active:bg-rose-50 mt-1"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </>
          ) : (
            <>
              <Link
                href="/#precos"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 py-3 px-2 rounded-lg active:bg-rose-50"
              >
                Planos
              </Link>
              <Link
                href="/login"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 py-3 px-2 rounded-lg active:bg-rose-50"
              >
                <LogIn className="h-4 w-4 text-[#E11D48]" />
                Entrar
              </Link>
              <Link href="/criar" onClick={() => setOpen(false)} className="mt-1">
                <Button className="w-full" size="default">
                  <Plus className="h-4 w-4 mr-1" />
                  Criar ILovePage
                </Button>
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

// Chip do usuário logado (desktop): avatar com inicial + nome + menu dropdown
// pra sair. Versão minimalista — sem popover lib pra manter leve.
function UserChip({
  firstName,
  initial,
  onLogout,
}: {
  firstName: string;
  initial: string;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-[#FFE4E6] transition-colors"
        aria-label="Menu do usuário"
        aria-expanded={open}
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#E11D48] to-[#881337] text-white font-black text-sm">
          {initial}
        </span>
        {firstName && (
          <span className="text-sm font-semibold text-[#1F2937] max-w-[120px] truncate">
            {firstName}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 rounded-2xl border border-rose-100 bg-white shadow-[0_10px_30px_rgba(136,19,55,0.08)] py-1.5 z-50">
          <button
            onMouseDown={(e) => { e.preventDefault(); onLogout(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#1F2937] hover:bg-[#FFE4E6] hover:text-[#E11D48] transition-colors text-left"
          >
            <LogOut className="h-4 w-4" />
            Sair da conta
          </button>
        </div>
      )}
    </div>
  );
}
